import React, { useEffect, useState } from 'react';
import { DollarSign } from 'lucide-react';
interface FinancialSummaryProps {
  totalAssets?: number;
  totalLiabilities?: number;
  monthlyIncome?: number;
  monthlyExpenses?: number;
  initialCash?: number;
  onCashChange?: (cash: number) => void;
}
export function FinancialSummary({
  totalAssets = 0,
  totalLiabilities = 0,
  monthlyIncome = 0,
  monthlyExpenses = 0,
  initialCash = 5000,
  onCashChange
}: FinancialSummaryProps) {
  const [totalCash, setTotalCash] = useState(initialCash);
  // Update totalCash when initialCash changes (e.g., when loaded from localStorage)
  useEffect(() => {
    setTotalCash(initialCash);
  }, [initialCash]);
  // Calculate derived values
  const netWorth = totalAssets - totalLiabilities;
  const cashFlow = monthlyIncome - monthlyExpenses;
  const handleCashChange = (valueStr: string) => {
    // Convert input to number, defaulting to 0 if invalid
    const numValue = valueStr === '' ? 0 : Number(valueStr) || 0;
    setTotalCash(numValue);
    // Notify parent component of cash change
    if (onCashChange) {
      onCashChange(numValue);
    }
  };
  const handlePayday = () => {
    const newCash = totalCash + cashFlow;
    setTotalCash(newCash);
    // Notify parent component of cash change
    if (onCashChange) {
      onCashChange(newCash);
    }
  };
  return <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100 w-full" data-id="element-121">
      <h2 className="text-xl font-semibold mb-4" data-id="element-122">Financial Summary</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-123">
        <div className="bg-gray-50 p-4 rounded-md" data-id="element-124">
          <p className="text-sm text-gray-500" data-id="element-125">Total Cash</p>
          <div className="flex items-center" data-id="element-126">
            <span className="mr-1" data-id="element-127">$</span>
            <input type="number" value={totalCash === 0 ? '' : totalCash} onChange={e => handleCashChange(e.target.value)} placeholder="0" className="text-2xl font-bold text-gray-800 bg-transparent border-b border-transparent focus:border-green-500 focus:outline-none w-full" data-id="element-128" />
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-md" data-id="element-129">
          <p className="text-sm text-gray-500" data-id="element-130">Net Worth</p>
          <p className="text-2xl font-bold text-green-600" data-id="element-131">
            ${netWorth.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-md" data-id="element-132">
          <p className="text-sm text-gray-500" data-id="element-133">Cash Flow</p>
          <p className="text-2xl font-bold text-green-600" data-id="element-134">
            ${cashFlow.toLocaleString()}
          </p>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-135">
        <div className="bg-gray-50 p-4 rounded-md" data-id="element-136">
          <button onClick={handlePayday} className="w-full py-2 px-4 bg-green-600 hover:bg-green-700 text-white font-medium rounded-md flex items-center justify-center transition-colors" data-id="element-137">
            <DollarSign size={18} className="mr-1" data-id="element-138" />
            Payday
          </button>
        </div>
        <div className="bg-gray-50 p-4 rounded-md" data-id="element-139">
          <p className="text-sm text-gray-500" data-id="element-140">Monthly Income</p>
          <p className="text-2xl font-bold text-gray-800" data-id="element-141">
            ${monthlyIncome.toLocaleString()}
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded-md" data-id="element-142">
          <p className="text-sm text-gray-500" data-id="element-143">Monthly Expenses</p>
          <p className="text-2xl font-bold text-gray-800" data-id="element-144">
            ${monthlyExpenses.toLocaleString()}
          </p>
        </div>
      </div>
    </div>;
}